import {
  Button,
  Heading,
  HStack,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { BaseLayout } from '..'
import { colors } from '../../../ui-kit'
import WebhookTable from '../../components/Developer/WebhookTable'
import WebhookCreateModal from '../../components/Developer/WebhookCreateModal'
import useToast from '../../hooks/useToast'
import type {
  WebhooksGetResponse,
  Webhook,
} from '../../interfaces/WebhooksGetResponse'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import WebhookDeleteModal from '../../components/Developer/WebhookDeleteModal'
import WebhookEditModal from '../../components/Developer/WebhookEditModal'

const WebhooksLayout = (): React.ReactElement => {
  const { t } = useTranslation()

  const { user } = useContext(UserContext)

  const toast = useToast()

  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [maxWebhooksPerClient, setMaxWebhooksPerClient] = useState(0)
  const [selectedWebhookId, setSelectedWebhookId] = useState<string | null>(
    null,
  )

  const webhookCreateModalDc = useDisclosure()
  const webhookEditModalDc = useDisclosure()
  const webhookDeleteModalDc = useDisclosure()

  const { request } = useFetch<WebhooksGetResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const loadWebhooks = (): void => {
    if (user?.clientNumber) {
      request
        .get(`/api/clients/${user.clientNumber}/settings/developer/webhook`)
        .then(response => {
          setWebhooks(response.webhooks)
          setMaxWebhooksPerClient(response.maxWebhooksPerClient)
        })
        .catch(error => {
          toast({
            title: t('error', 'Error'),
            description: error.message,
            status: 'error',
            duration: 9000,
            isClosable: true,
          })
        })
    }
  }

  useEffect(() => {
    loadWebhooks()
  }, [user])

  return (
    <BaseLayout>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="600"
          color={colors.appDarkIndigo}
        >
          {t('webhooks', 'Webhooks')}
        </Heading>
        <HStack spacing="1em">
          {maxWebhooksPerClient <= webhooks.length ? (
            <Tooltip
              label={t(
                'max_webhooks_limit_reached',
                'Maximum webhooks limit reached',
              )}
              shouldWrapChildren
              mt="3"
            >
              <Button isDisabled>{t('plus_add_new', '+ Add new')}</Button>
            </Tooltip>
          ) : (
            <Button onClick={webhookCreateModalDc.onOpen}>
              {t('plus_add_new', '+ Add new')}
            </Button>
          )}
        </HStack>
      </MainContentHeadingBox>
      <MainContentWrapper style={{ gap: '0.5em' }}>
        <WebhookTable
          loading={request.loading}
          webhooks={webhooks
            // sort in decending order by createdAt - newest first
            .sort(({ createdAt: a }, { createdAt: b }) =>
              new Date(a) < new Date(b) ? 1 : -1,
            )}
          openDeleteModal={(id: string) => {
            setSelectedWebhookId(id)
            webhookDeleteModalDc.onOpen()
          }}
          openEditModal={(id: string) => {
            setSelectedWebhookId(id)
            webhookEditModalDc.onOpen()
          }}
        />
      </MainContentWrapper>
      <WebhookCreateModal
        existingWebhookEndpoints={webhooks.map(({ endpoint }) => endpoint)}
        isOpen={webhookCreateModalDc.isOpen}
        reloadWebhooks={loadWebhooks}
        onClose={webhookCreateModalDc.onClose}
        onWebhookCreate={(webhook: Webhook) => {
          setWebhooks([...webhooks, webhook])
        }}
      />
      <WebhookEditModal
        existingWebhookEndpoints={webhooks.map(({ endpoint }) => endpoint)}
        webhook={webhooks.find(w => w.id === selectedWebhookId)!}
        isOpen={webhookEditModalDc.isOpen}
        reloadWebhooks={loadWebhooks}
        onClose={webhookEditModalDc.onClose}
        onWebhookUpdate={(webhookId, set) => {
          setWebhooks(
            webhooks.map(w => {
              if (w.id === webhookId) {
                return { ...w, ...set }
              }
              return w
            }),
          )
        }}
      />
      <WebhookDeleteModal
        webhook={webhooks.find(w => w.id === selectedWebhookId)}
        isOpen={webhookDeleteModalDc.isOpen}
        reloadWebhooks={loadWebhooks}
        onClose={webhookDeleteModalDc.onClose}
        onWebhookDelete={(id: string) => {
          setWebhooks(webhooks.filter(webhook => webhook.id !== id))
        }}
      />
    </BaseLayout>
  )
}

export default WebhooksLayout
